import React, { useEffect, useMemo, useState } from 'react';
import {
  Loader,
  Upload,
  CheckCircle,
  X,
  XCircle,
  AlertTriangle,
  Download,
  Save
} from 'lucide-react';
import {
  custodianCouponPaymentsAPI,
  custodianAPI,
  couponDistributionRequestsAPI,
  formatCurrency,
  formatDate
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const parsePaymentCSV = async (file) => {
  const text = await file.text();
  const lines = text.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV file is empty');
  }

  const headers = lines[0].split(',').map((h) => h.trim());
  const requiredHeaders = [
    'Investor ID',
    'Investor Name',
    'Payment Amount',
    'LEI',
    'Percentage',
    'Payment Type',
    'Payment Date'
  ];
  
  // Payment Reference is optional
  const optionalHeaders = ['Payment Reference'];

  const missing = requiredHeaders.filter((header) => !headers.includes(header));
  if (missing.length) {
    throw new Error(`Missing required headers: ${missing.join(', ')}`);
  }

  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const values = line.split(',').map((value) => value.trim());
    const row = {};
    headers.forEach((header, index) => {
      row[header] = values[index];
    });
    rows.push(row);
  }

  return rows;
};

const getNormalizedStatus = (status) => {
  switch (status) {
    case 'Pending Payment':
      return 'Pending';
    case 'Ready for Distribution':
      return 'Ready';
    case 'Completed':
      return 'Completed';
    default:
      return status;
  }
};

const getIssuerDisplay = (bond) => {
  if (!bond || !bond.issuer) return '-';
  const issuer = bond.issuer;
  if (typeof issuer === 'string') {
    // Raw ObjectId – don't show it directly
    return '-';
  }
  return (
    issuer.email ||
    issuer.corporateInfo?.companyName ||
    issuer.corporateInfo?.legalEntityName ||
    issuer.name ||
    '-'
  );
};

// Safely convert various number/Decimal128 shapes into a plain JS number
const getNumericValue = (value) => {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === 'object') {
    if ('$numberDecimal' in value) {
      const parsed = Number(value.$numberDecimal);
      return Number.isFinite(parsed) ? parsed : 0;
    }
    if ('$numberDouble' in value) {
      const parsed = Number(value.$numberDouble);
      return Number.isFinite(parsed) ? parsed : 0;
    }
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const StatusBadge = ({ paymentStatus, approvalStatus }) => {
  if (approvalStatus === 'pending_l2') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        Pending L2
      </span>
    );
  }

  if (approvalStatus === 'pending_l1') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        Pending L1
      </span>
    );
  }

  if (approvalStatus === 'approved') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Approved
      </span>
    );
  }

  if (approvalStatus === 'rejected') {
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        Rejected
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
      {getNormalizedStatus(paymentStatus)}
    </span>
  );
};

const getApproverDisplay = (user, timestamp) => {
  if (!user) return '-';
  const email = user.email || user.name || 'User';
  if (!timestamp) return email;
  return (
    <div>
      <div className="text-sm font-medium text-gray-900">{email}</div>
      <div className="text-xs text-gray-500">
        {formatDate(timestamp)}{' '}
        {new Date(timestamp).toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit' })}
      </div>
    </div>
  );
};

const CustodianCouponProfitDistribution = () => {
  const { user } = useAuth();
  const currentUserId = user?._id || user?.id;

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeView, setActiveView] = useState('list');

  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [pendingApprovalsLoading, setPendingApprovalsLoading] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPaymentForUpload, setSelectedPaymentForUpload] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvStats, setCsvStats] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showPreview, setShowPreview] = useState(false);

  const [approvingPayment, setApprovingPayment] = useState(null);
  const [isAutoDistributing, setIsAutoDistributing] = useState(false);
  const [showAutoModal, setShowAutoModal] = useState(false);
  const [autoModalPayment, setAutoModalPayment] = useState(null);
  const [autoRows, setAutoRows] = useState([]);
  const [autoRemainingAmount, setAutoRemainingAmount] = useState(0);
  const [autoError, setAutoError] = useState(null);
  const [rejectingPayment, setRejectingPayment] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionComments, setRejectionComments] = useState('');

  // State for coupon distribution requests (FA -> Custodian) that create these payments
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [requestsError, setRequestsError] = useState(null);
  const [approvingRequestId, setApprovingRequestId] = useState(null);

  const fetchPayments = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await custodianCouponPaymentsAPI.getPayments();
      const data = response.data?.data || response.data || [];
      setPayments(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch payments.');
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      setPendingApprovalsLoading(true);
      const response = await custodianCouponPaymentsAPI.getPendingApprovals();
      const data = response.data?.data || response.data || [];
      setPendingApprovals(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching pending approvals:', err);
      setPendingApprovals([]);
    } finally {
      setPendingApprovalsLoading(false);
    }
  };

  const fetchRequests = async () => {
    try {
      setRequestsLoading(true);
      setRequestsError(null);
      // Get all statuses and filter client-side; backend already scopes to this custodian as holder
      const response = await couponDistributionRequestsAPI.getMyRequests({ status: 'all' });
      const data = response.data?.data || response.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching coupon distribution requests for custodian:', err);
      setRequests([]);
      setRequestsError(err?.response?.data?.message || err.message || 'Failed to fetch distribution requests.');
    } finally {
      setRequestsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
    fetchRequests();
  }, []);

  useEffect(() => {
    if (activeView === 'pending-approvals') {
      fetchPendingApprovals();
    }
  }, [activeView]);

  const upcomingPayments = useMemo(() => {
    return payments.slice().sort((a, b) => new Date(a.paymentDate) - new Date(b.paymentDate));
  }, [payments]);

  // Only show requests that are still awaiting L1/L2 approval
  const pendingRequests = useMemo(() => {
    return requests.filter(
      (r) => r.status === 'pending' || r.status === 'pending_l2'
    );
  }, [requests]);

  // Combined view: pending requests first, then payment schedules
  const combinedRows = useMemo(() => {
    const requestRows = pendingRequests.map((r) => ({
      ...r,
      _rowKind: 'request'
    }));
    const paymentRows = upcomingPayments.map((p) => ({
      ...p,
      _rowKind: 'payment'
    }));
    return [...requestRows, ...paymentRows];
  }, [pendingRequests, upcomingPayments]);

  const getUserIdValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    return value._id || value.id || null;
  };

  const canApproveRequestL2 = (request) => {
    if (request.status !== 'pending_l2') return true;
    const l1ApproverId = getUserIdValue(request.l1ApprovedBy);
    if (!l1ApproverId) return true;
    if (!currentUserId) return false;
    return l1ApproverId.toString() !== currentUserId.toString();
  };

  // Check if current user is the initiator (L1 approver) of a payment
  const isCurrentUserInitiator = (payment) => {
    if (!payment || !currentUserId) return false;
    const l1ApproverId = getUserIdValue(payment.l1ApprovedBy);
    if (!l1ApproverId) return false;
    return l1ApproverId.toString() === currentUserId.toString();
  };

  const getRequestActionLabel = (request) =>
    request.status === 'pending_l2' ? 'Approve L2' : 'Approve';

  const handleApproveRequest = async (request) => {
    try {
      setApprovingRequestId(request._id);
      if (request.status === 'pending') {
        await couponDistributionRequestsAPI.approveRequestL1(request._id);
      } else if (request.status === 'pending_l2') {
        await couponDistributionRequestsAPI.approveRequestL2(request._id);
      }
      // After approval, refresh both requests and payments so the row effectively becomes a payment row
      await fetchRequests();
      await fetchPayments();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(
        err?.response?.data?.message ||
          err.message ||
          'Failed to approve coupon distribution request.'
      );
    } finally {
      setApprovingRequestId(null);
    }
  };

  const handleSelectPayment = (payment) => {
    setSelectedPaymentForUpload(payment);
    setUploadedFile(null);
    setAllocations([]);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setUploadProgress(0);
    setShowUploadModal(true);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setSelectedPaymentForUpload(null);
    setUploadedFile(null);
    setAllocations([]);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setUploadProgress(0);
  };

  const getRemainingAmount = (payment) => {
    if (!payment) return 0;
    if (payment.remainingAmount !== undefined && payment.remainingAmount !== null) {
      return Number(payment.remainingAmount);
    }
    if (payment.amount !== undefined && payment.amount !== null) {
      return Number(payment.amount);
    }
    if (payment.actualPaymentAmount !== undefined && payment.actualPaymentAmount !== null) {
      return Number(payment.actualPaymentAmount);
    }
    if (payment.allocatedAmount !== undefined && payment.allocatedAmount !== null) {
      return Number(payment.allocatedAmount);
    }
    return 0;
  };

  const downloadTemplate = () => {
    const csvContent = [
      'Investor ID,Investor Name,Payment Amount,LEI,Percentage,Payment Type,Payment Reference,Payment Date',
      '6895e4fb671625f8fa621805,Sub Investor 3,250000,12345678901234567890,25.00,Profit Distribution,TXN-2025-001,2025-07-22',
      '689463871e48b343c0e5e99e,Sub Investor 1,150000,09876543210987654321,15.00,Profit Distribution,TXN-2025-002,2025-07-22',
      '6895545d734c54916db03292,Sub Investor 2,100000,11223344556677889900,10.00,Profit Distribution,TXN-2025-003,2025-07-22'
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'custodian_coupon_distribution_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      alert('Please upload a CSV file only.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size must be less than 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);
    setCsvErrors([]);
    setCsvStats(null);
    setShowPreview(false);
    setAllocations([]);

    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 90) {
          clearInterval(progressInterval);
          return 90;
        }
        return prev + 10;
      });
    }, 100);

    try {
      const rows = await parsePaymentCSV(file);
      clearInterval(progressInterval);
      setUploadProgress(100);

      if (!rows || rows.length === 0) {
        throw new Error('CSV file does not contain any data rows.');
      }

      const totalAmount = rows.reduce(
        (sum, row) => sum + Number(row['Payment Amount'] || row.paymentAmount || 0),
        0
      );
      const totalPercentage = rows.reduce(
        (sum, row) => sum + Number(row['Percentage'] || row.percentage || 0),
        0
      );

      setUploadedFile(file);
      setAllocations(rows);
      setCsvStats({
        totalRows: rows.length,
        validRows: rows.length,
        totalAmount,
        totalPercentage
      });
      setCsvErrors([]);
      setShowPreview(true);
    } catch (err) {
      clearInterval(progressInterval);
      setUploadedFile(null);
      setAllocations([]);
      setCsvStats(null);
      setShowPreview(false);
      setCsvErrors([err.message || 'Failed to process CSV file.']);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const autoTotalAmount = useMemo(
    () => autoRows.reduce((sum, row) => sum + (Number(row.amount) || 0), 0),
    [autoRows]
  );

  const handleAutoAmountChange = (index, value) => {
    const next = [...autoRows];
    let cleaned = value;
    if (typeof cleaned === 'string') {
      // Remove leading zeros but keep a single zero if that's the only digit
      cleaned = cleaned.replace(/^0+(?=\d)/, '');
    }
    next[index] = {
      ...next[index],
      amount: cleaned
    };
    setAutoRows(next);
  };

  const handleConfirmAutoDistribute = async () => {
    if (!autoModalPayment) return;
    const total = autoTotalAmount;
    if (total <= 0) {
      setAutoError('Total distributed amount must be greater than zero.');
      return;
    }
    if (total - autoRemainingAmount > 0.01) {
      setAutoError('Total distributed amount cannot exceed remaining amount.');
      return;
    }

    try {
      setAutoError(null);
      setIsAutoDistributing(true);

      const subInvestorPayments = autoRows
        .filter((row) => Number(row.amount) > 0)
        .map((row) => ({
          investorId: row.investorId,
          investorName: row.investorName,
          walletAddress: row.walletAddress,
          paymentAmount: Number(row.amount),
          allocationPercentage:
            autoRemainingAmount > 0 ? (Number(row.amount) / autoRemainingAmount) * 100 : 0
        }));

      await custodianCouponPaymentsAPI.autoDistributeSubInvestorPayments(
        autoModalPayment._id,
        { subInvestorPayments }
      );

      alert('Auto distribution staged successfully. Payment is now pending L2 approval.');
      setShowAutoModal(false);
      setAutoModalPayment(null);
      setAutoRows([]);
      await fetchPayments();
      await fetchPendingApprovals();
    } catch (err) {
      setAutoError(
        err?.response?.data?.message ||
          err.message ||
          'Failed to stage auto-distributed sub-investor payments.'
      );
    } finally {
      setIsAutoDistributing(false);
    }
  };

  const handleValidateAndUpload = async () => {
    if (!selectedPaymentForUpload) {
      setCsvErrors(['No payment selected.']);
      return;
    }

    if (!uploadedFile) {
      setCsvErrors(['Please upload a CSV file before submitting.']);
      return;
    }

    if (!allocations.length) {
      setCsvErrors(['Please upload a CSV file with at least one valid row.']);
      return;
    }

    const currentRemaining = getRemainingAmount(selectedPaymentForUpload);
    const csvTotalAmount =
      csvStats?.totalAmount ||
      allocations.reduce(
        (sum, row) => sum + Number(row['Payment Amount'] || row.paymentAmount || 0),
        0
      );

    if (csvTotalAmount > currentRemaining + 0.01) {
      setCsvErrors([
        `Total payment amount in CSV (${formatCurrency(csvTotalAmount)}) cannot exceed the remaining amount (${formatCurrency(
          currentRemaining
        )}).`
      ]);
      return;
    }

    setCsvErrors([]);
    try {
      setIsUploading(true);
      setUploadProgress(20);

      const bondId = selectedPaymentForUpload.bondId?._id || selectedPaymentForUpload.bondId;
      
      // Transform CSV data to camelCase format expected by validation API
      const csvDataForValidation = allocations.map((row) => ({
        investorId: row['Investor ID'] || row.investorId,
        lei: row['LEI'] || row.lei
      }));
      
      await custodianAPI.validateInvestorWallet(csvDataForValidation, bondId);
      setUploadProgress(60);

      const uploadData = {
        subInvestorPayments: allocations.map((row) => ({
          investorId: row['Investor ID'] || row.investorId,
          investorName: row['Investor Name'] || row.investorName,
          paymentAmount: Number(row['Payment Amount'] || row.paymentAmount || 0),
          lei: row['LEI'] || row.lei,
          allocationPercentage: Number(row['Percentage'] || row.percentage || 0),
          paymentReference: row['Payment Reference'] || row.paymentReference
        }))
      };

      await custodianCouponPaymentsAPI.uploadSubInvestorPayments(
        selectedPaymentForUpload._id || selectedPaymentForUpload.id,
        uploadData
      );

      setUploadProgress(100);
      alert('Sub-investor payments uploaded successfully. Pending L2 approval.');
      closeUploadModal();
      fetchPayments();
    } catch (err) {
      console.error('Upload error:', err);
      setCsvErrors([err.response?.data?.message || err.message || 'Failed to upload payments.']);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const confirmApproveL2 = async () => {
    if (!approvingPayment) return;
    try {
      await custodianCouponPaymentsAPI.approvePaymentL2(approvingPayment._id, { comments: '' });
      alert('Payment distribution approved successfully.');
      setApprovingPayment(null);
      fetchPendingApprovals();
      fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to approve payment.');
    }
  };

  const handleAutoDistribute = async (payment) => {
    if (!payment) return;
    try {
      setIsAutoDistributing(true);
      setAutoError(null);

      const response = await custodianCouponPaymentsAPI.autoDistributePreview(payment._id);
      const data = response?.data?.data || response?.data || {};
      const rows = Array.isArray(data.rows) ? data.rows : [];

      if (!rows.length) {
        setAutoError('No eligible sub-investor holdings found for this payment.');
        setShowAutoModal(false);
        return;
      }

      setAutoModalPayment(payment);
      // Map to editable rows with an "amount" field (stored as string to control formatting)
      setAutoRows(
        rows.map((row) => ({
          investorId: row.investorId,
          investorName: row.investorName,
          walletAddress: row.walletAddress,
          tokenAmount: row.tokenAmount,
          percentageOfTokens: row.percentageOfTokens,
          amount: row.suggestedAmount != null ? String(row.suggestedAmount) : ''
        }))
      );
      setAutoRemainingAmount(Number(data.remainingAmount || 0));
      setShowAutoModal(true);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err.message || 'Failed to load auto-distribution preview.');
      setShowAutoModal(false);
    } finally {
      setIsAutoDistributing(false);
    }
  };

  const confirmReject = async () => {
    if (!rejectingPayment) return;
    try {
      await custodianCouponPaymentsAPI.rejectPayment(rejectingPayment._id, {
        rejectionReason,
        comments: rejectionComments
      });
      alert('Payment distribution rejected.');
      setRejectingPayment(null);
      setRejectionReason('');
      setRejectionComments('');
      fetchPendingApprovals();
      fetchPayments();
    } catch (err) {
      alert(err.response?.data?.message || err.message || 'Failed to reject payment.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 py-8 mx-auto space-y-6 max-w-7xl sm:px-6 lg:px-8">
        <div className="space-y-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Coupon Management</h1>
            <p className="text-sm text-gray-600">
              Manage scheduled coupon payments
            </p>
          </div>

          <div className="border-b border-gray-200">
            <nav className="flex -mb-px space-x-8">
              <button
                onClick={() => setActiveView('list')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeView === 'list'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Payments
              </button>
              <button
                onClick={() => setActiveView('pending-approvals')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeView === 'pending-approvals'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approvals
              </button>
            </nav>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              {activeView === 'list' ? 'Payment Schedules' : 'Pending Payment Approvals'}
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              {activeView === 'list'
                ? `${upcomingPayments.length} payment schedule${upcomingPayments.length !== 1 ? 's' : ''} found`
                : (() => {
                    // Calculate total including initiator's pending payments
                    const initiatorPendingPayments = payments.filter(
                      (p) => 
                        p.approvalStatus === 'pending_l2' && 
                        isCurrentUserInitiator(p)
                    );
                    const allPendingCount = pendingApprovals.length + 
                      initiatorPendingPayments.filter(
                        (p) => !pendingApprovals.some(
                          (pa) => (pa._id || pa.id) === (p._id || p.id)
                        )
                      ).length;
                    return `${allPendingCount} pending approval${allPendingCount !== 1 ? 's' : ''} found`;
                  })()}
            </p>
          </div>

          <div >
            {activeView === 'list' ? (
              loading || requestsLoading ? (
              <div className="flex items-center justify-center py-16 text-gray-500">
                <Loader className="w-5 h-5 mr-2 animate-spin" />
                Loading requests and payments...
              </div>
            ) : error ? (
              <div className="py-8 text-sm text-center text-red-600">{error}</div>
            ) : requestsError ? (
              <div className="py-8 text-sm text-center text-red-600">{requestsError}</div>
            ) : combinedRows.length === 0 ? (
              <div className="py-12 text-sm text-center text-gray-500">No payments found.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Bond Details</th>
                      <th className="table-header-cell">Issuer</th>
                      <th className="table-header-cell">Payment Date</th>
                     
                      <th className="table-header-cell">Total Amount</th>
                      <th className="table-header-cell">
                      L1 Initiator
                    </th>
                    <th className="table-header-cell">
                      L2 Approver
                    </th>
                      <th className="table-header-cell">Status</th>
                      <th className="table-header-cell ">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {combinedRows.map((row) => {
                      const isRequest = row._rowKind === 'request';
                      const key = isRequest ? `request-${row._id}` : `payment-${row._id}`;
                      const bond = row.bondId || {};
                      return (
                      <tr key={key}>
                        <td className="table-cell">
                          <div className="font-medium">{bond.bondName || 'Unknown Bond'}</div>
                          <div className="text-xs text-gray-500">{bond.isin || '-'}</div>
                        </td>
                        <td className="table-cell">
                          {getIssuerDisplay(bond)}
                        </td>
                        <td className="table-cell">{formatDate(row.paymentDate)}</td>
                      
                        <td className="table-cell">
                          {isRequest ? (
                            formatCurrency(getNumericValue(row.amount))
                          ) : (
                            <div className="flex flex-col">
                              <span>{formatCurrency(row.amount)}</span>
                              <span className="text-xs text-gray-500">
                                Remaining: {formatCurrency(getRemainingAmount(row))}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          {isRequest
                            ? '-'
                            : getApproverDisplay(row.l1ApprovedBy, row.l1ApprovedAt)}
                        </td>
                        <td className="table-cell">
                          {isRequest
                            ? '-'
                            : getApproverDisplay(row.l2ApprovedBy, row.l2ApprovedAt)}
                        </td>
                        <td className="table-cell">
                          {isRequest ? (
                            <StatusBadge
                              paymentStatus={null}
                              approvalStatus={
                                row.status === 'pending'
                                  ? 'pending_l1'
                                  : row.status === 'pending_l2'
                                  ? 'pending_l2'
                                  : row.status
                              }
                            />
                          ) : (
                            <StatusBadge
                              paymentStatus={row.paymentStatus}
                              approvalStatus={row.approvalStatus}
                            />
                          )}
                        </td>
                        <td className="table-cell space-x-2 text-right">
                          {isRequest ? (
                            <>
                              {/* Hide L2 button from the L1 initiator; they should not see Approve L2 at all */}
                              {!(row.status === 'pending_l2' && !canApproveRequestL2(row)) && (
                                <button
                                  onClick={() => handleApproveRequest(row)}
                                  disabled={approvingRequestId === row._id}
                                  className="inline-flex items-center btn-primary btn-sm"
                                >
                                  {approvingRequestId === row._id
                                    ? 'Approving...'
                                    : getRequestActionLabel(row)}
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              {(() => {
                                const remaining = getRemainingAmount(row);
                                const disabledAuto =
                                  isAutoDistributing ||
                                  row.approvalStatus === 'pending_l2' ||
                                  remaining <= 0;
                                const disabledUpload =
                                  row.approvalStatus === 'pending_l2' || remaining <= 0;
                                const autoTitle =
                                  row.approvalStatus === 'pending_l2'
                                    ? 'Already pending L2 approval'
                                    : remaining <= 0
                                    ? 'No remaining amount to distribute'
                                    : 'Automatically distribute to sub-investors based on holdings';
                                const uploadTitle =
                                  row.approvalStatus === 'pending_l2'
                                    ? 'Waiting for L2 approval before new uploads are allowed'
                                    : remaining <= 0
                                    ? 'No remaining amount to distribute'
                                    : 'Upload payment distribution via CSV';

                                return (
                                  <div className="flex items-center justify-end space-x-2">
                                    <button
                                      onClick={true ? () => handleAutoDistribute(row) : undefined}
                                      
                                      className={`btn-secondary btn-sm ${
                                        disabledAuto ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                      title={autoTitle}
                                    >
                                      {isAutoDistributing ? 'Auto Distributing...' : 'Auto Distribute'}
                                    </button>
                                    <button
                                      onClick={true ? () => handleSelectPayment(row) : undefined}
                                      className={`btn-primary btn-sm ${
                                        disabledUpload ? 'opacity-50 cursor-not-allowed' : ''
                                      }`}
                                      
                                      title={uploadTitle}
                                    >
                                      <Upload className="w-4 h-4 mr-1" />
                                      Upload Payment
                                    </button>
                                  </div>
                                );
                              })()}
                            </>
                          )}
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            )
          ) : pendingApprovalsLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <Loader className="w-5 h-5 mr-2 animate-spin" />
              Loading pending approvals...
            </div>
          ) : (() => {
            // Include payments where current user is the initiator and status is pending_l2
            const initiatorPendingPayments = payments.filter(
              (p) => 
                p.approvalStatus === 'pending_l2' && 
                isCurrentUserInitiator(p)
            );
            
            // Combine pending approvals from API with initiator's own pending payments
            // Deduplicate by payment _id
            const allPendingApprovals = [...pendingApprovals];
            initiatorPendingPayments.forEach((payment) => {
              const exists = allPendingApprovals.some(
                (pa) => (pa._id || pa.id) === (payment._id || payment.id)
              );
              if (!exists) {
                allPendingApprovals.push(payment);
              }
            });
            
            return allPendingApprovals.length === 0 ? (
              <div className="py-12 text-sm text-center text-gray-500">No pending approvals.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="table">
                  <thead className="table-header">
                    <tr>
                      <th className="table-header-cell">Bond Details</th>
                      <th className="table-header-cell">Issuer</th>
                      <th className="table-header-cell">Payment Date</th>
                     
                      <th className="table-header-cell">Total Amount</th>
                      <th className="table-header-cell">L1 Initiator</th>
                      <th className="table-header-cell">L2 Approval</th>
                      <th className="table-header-cell">Status</th>
                      <th className="text-right table-header-cell">Action</th>
                    </tr>
                  </thead>
                  <tbody className="table-body">
                    {allPendingApprovals.map((payment) => (
                    <tr key={payment._id}>
                      <td className="table-cell">
                        <div className="font-medium">{payment.bondId?.bondName || 'Unknown Bond'}</div>
                        <div className="text-xs text-gray-500">{payment.bondId?.isin || '-'}</div>
                      </td>
                      <td className="table-cell">
                        {getIssuerDisplay(payment.bondId)}
                      </td>
                      <td className="table-cell">{formatDate(payment.paymentDate)}</td>
                    
                      <td className="table-cell">{formatCurrency(payment.amount)}</td>
                      <td className="table-cell">
                        {getApproverDisplay(payment.l1ApprovedBy, payment.l1ApprovedAt)}
                      </td>
                      <td className="table-cell">
                        {getApproverDisplay(payment.l2ApprovedBy, payment.l2ApprovedAt)}
                      </td>
                      <td className="table-cell">
                        <StatusBadge
                          paymentStatus={payment.paymentStatus}
                          approvalStatus={payment.approvalStatus}
                        />
                      </td>
                      <td className="table-cell space-x-2 text-right">
                        {(() => {
                          const isInitiator = isCurrentUserInitiator(payment);
                          // Hide approve/reject buttons for the initiator
                          if (isInitiator) {
                            return null;
                          }
                          return (
                            <>
                              <button
                                onClick={true ? () => setApprovingPayment(payment) : undefined}
                                
                                className="inline-flex items-center btn-primary btn-sm"
                                title="Approve L2"
                              >
                               
                                Approve L2
                              </button>
                              <button
                                onClick={true ? () => setRejectingPayment(payment) : undefined}
                                
                                className="inline-flex items-center btn-secondary btn-sm"
                                title="Reject"
                              >
                              
                                Reject
                              </button>
                            </>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Auto Distribute Modal */}
      {showAutoModal && autoModalPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-medium text-gray-900">
                  Auto Distribute to Sub-Investors
                </h3>
                <p className="mt-1 text-xs text-gray-600">
                  Adjust the amounts for each sub-investor. The total distributed amount
                  must not exceed the remaining amount.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAutoModal(false);
                  setAutoModalPayment(null);
                  setAutoRows([]);
                  setAutoError(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-4 mb-2 text-sm">
                <div>
                  <div className="text-gray-500">Bond</div>
                  <div className="font-medium">
                    {autoModalPayment.bondId?.bondName || 'Unknown Bond'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Payment Date</div>
                  <div className="font-medium">
                    {formatDate(autoModalPayment.paymentDate)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Remaining Amount</div>
                  <div className="font-medium">
                    {formatCurrency(autoRemainingAmount)}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Total Distributed (This Round)</div>
                  <div
                    className={`font-medium ${
                      autoTotalAmount - autoRemainingAmount > 0.01 ? 'text-red-600' : 'text-gray-900'
                    }`}
                  >
                    {formatCurrency(autoTotalAmount)}
                  </div>
                </div>
              </div>

              {autoError && (
                <div className="p-3 mb-3 text-xs text-red-700 border border-red-200 rounded bg-red-50">
                  {autoError}
                </div>
              )}

              <div className="overflow-x-auto border border-gray-200 rounded">
                <table className="min-w-full text-xs divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 font-medium text-left text-gray-700">
                        Sub-Investor
                      </th>
                      <th className="px-3 py-2 font-medium text-right text-gray-700">
                        Tokens
                      </th>
                      <th className="px-3 py-2 font-medium text-right text-gray-700">
                        % of Tokens
                      </th>
                      <th className="px-3 py-2 font-medium text-right text-gray-700">
                        Amount (MYR)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {autoRows.map((row, index) => (
                      <tr key={row.investorId || index}>
                        <td className="px-3 py-2">
                          <div className="font-medium text-gray-900">
                            {row.investorName || row.investorId || '—'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {row.walletAddress || ''}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(row.tokenAmount || 0).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {(row.percentageOfTokens || 0).toFixed(4)}%
                        </td>
                        <td className="px-3 py-2 text-right">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={row.amount ?? ''}
                            onChange={(e) => handleAutoAmountChange(index, e.target.value)}
                            className="px-2 py-1 text-xs text-right border border-gray-300 rounded w-28 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end px-6 py-4 space-x-3 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAutoModal(false);
                  setAutoModalPayment(null);
                  setAutoRows([]);
                  setAutoError(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmAutoDistribute}
                disabled={isAutoDistributing || autoRows.length === 0}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isAutoDistributing ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Confirm Auto Distribute
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showUploadModal && selectedPaymentForUpload && (() => {
        const currentRemaining = getRemainingAmount(selectedPaymentForUpload);
        const remainingAmountDisplay = formatCurrency(currentRemaining);
        const csvTotalAmount =
          csvStats?.totalAmount ||
          allocations.reduce(
            (sum, row) => sum + Number(row['Payment Amount'] || row.paymentAmount || 0),
            0
          );
        const totalPercentage =
          csvStats?.totalPercentage ||
          allocations.reduce(
            (sum, row) => sum + Number(row['Percentage'] || row.percentage || 0),
            0
          );
        const isAmountExceeded = csvStats && csvTotalAmount > currentRemaining + 0.01;

        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      Upload Payment Distribution for {selectedPaymentForUpload.bondId?.bondName || 'N/A'}
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Upload the sub-investor distribution CSV and supporting proof for this payment.
                    </p>
                  </div>
                  <button onClick={closeUploadModal} className="text-gray-400 hover:text-gray-600">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              <div className="flex-1 px-6 py-6 overflow-y-auto">
                <div className="p-4 mb-6 rounded-lg bg-gray-50">
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Summary</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Bond Name:</span>
                      <div className="font-medium">
                        {selectedPaymentForUpload.bondId?.bondName || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Issuer:</span>
                      <div className="font-medium">
                        {getIssuerDisplay(selectedPaymentForUpload.bondId)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Payment Date:</span>
                      <div className="font-medium">
                        {formatDate(selectedPaymentForUpload.paymentDate)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Payment Type:</span>
                      <div className="font-medium">
                        {selectedPaymentForUpload.paymentType || '-'}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Total Amount:</span>
                      <div className="font-medium">
                        {formatCurrency(selectedPaymentForUpload.amount)}
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-600">Remaining Amount:</span>
                      <div className="font-medium">{remainingAmountDisplay}</div>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Upload Payment Distribution CSV</h4>
                  <div className="p-6 text-center transition-colors border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400">
                    <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                    <div className="mb-4 text-sm text-gray-600">
                      <span className="font-medium cursor-pointer text-primary-600 hover:text-primary-500">
                        Click to upload
                      </span>{' '}
                      or drag and drop your CSV file here
                    </div>
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="custodian-coupon-upload"
                    />
                    <label
                      htmlFor="custodian-coupon-upload"
                      className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                    >
                      Choose File
                    </label>
                    <p className="mt-2 text-xs text-gray-500">CSV files only, max 5MB</p>

                    <div className="p-3 mt-4 text-left border border-blue-200 rounded-md bg-blue-50">
                      <h5 className="mb-2 text-sm font-medium text-blue-800">
                        CSV Format Requirements:
                      </h5>
                      <ul className="space-y-1 text-xs text-blue-700">
                        <li>
                          • Required Headers: Investor ID, Investor Name, Payment Amount, LEI,
                          Percentage, Payment Type, Payment Date
                        </li>
                        <li>
                          • Optional Headers: Payment Reference (can be omitted if not needed)
                        </li>
                        <li>• Payment Amount: Numbers only (no commas), e.g., 250000</li>
                        <li>
                          • Total Payment Amount: Cannot exceed the Remaining Amount shown above
                        </li>
                        <li>• LEI: Must be exactly 20 characters and match the sub-investor's registered Legal Entity Identifier</li>
                        <li>• Percentage: Individual allocation percentage for each investor</li>
                        <li>• Payment Date: YYYY-MM-DD format</li>
                      </ul>
                    </div>
                  </div>

                  {isUploading && (
                    <div className="mt-4">
                      <div className="flex items-center mb-2">
                        <Loader className="w-4 h-4 mr-2 animate-spin text-primary-600" />
                        <span className="text-sm text-gray-600">Processing CSV file...</span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 transition-all duration-300 rounded-full bg-primary-600"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {uploadedFile && !isUploading && (
                    <div className="flex items-center justify-between p-3 mt-4 border border-blue-200 rounded-md bg-blue-50">
                      <div>
                        <div className="text-sm font-medium text-blue-800">{uploadedFile.name}</div>
                        <div className="text-xs text-blue-600">
                          {(uploadedFile.size / 1024).toFixed(1)} KB • Uploaded successfully
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setUploadedFile(null);
                          setAllocations([]);
                          setCsvErrors([]);
                          setCsvStats(null);
                          setShowPreview(false);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>

                {csvErrors.length > 0 && (
                  <div className="p-4 mb-6 border border-red-200 rounded-md bg-red-50">
                    <div className="flex items-start">
                      <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                      <div className="flex-1">
                        <h5 className="mb-2 text-sm font-medium text-red-800">
                          {csvErrors.length === 1
                            ? 'CSV Validation Issue'
                            : `${csvErrors.length} CSV Validation Issues`}
                        </h5>
                        <div className="space-y-1 overflow-y-auto text-xs text-red-700 max-h-32">
                          {csvErrors.map((err, idx) => (
                            <div key={idx}>• {err}</div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {csvStats && (
                  <div
                    className={`mb-6 p-4 border rounded-md ${
                      isAmountExceeded ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'
                    }`}
                  >
                    <div className="flex items-start">
                      {isAmountExceeded ? (
                        <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                      )}
                      <div className="flex-1 text-sm">
                        <h4
                          className={`font-medium mb-2 ${
                            isAmountExceeded ? 'text-red-800' : 'text-green-800'
                          }`}
                        >
                          CSV Processing Summary
                        </h4>
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div>Total Rows: {csvStats.totalRows}</div>
                          <div>Valid Rows: {csvStats.validRows || allocations.length}</div>
                          <div>Total Amount: {formatCurrency(csvTotalAmount)}</div>
                          <div>Total Percentage: {(totalPercentage || 0).toFixed(2)}%</div>
                          <div className="col-span-2 font-medium">
                            Amount Validation:{' '}
                            {isAmountExceeded
                              ? `❌ Amount exceeds remaining balance (Remaining: ${formatCurrency(
                                  currentRemaining
                                )})`
                              : `✅ Amount within remaining balance (Remaining: ${formatCurrency(
                                  currentRemaining
                                )})`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {showPreview && allocations.length > 0 && (
                  <div className="mb-6">
                    <h4 className="mb-3 text-sm font-medium text-gray-900">Payment Distribution Preview</h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-md">
                      <table className="table">
                        <thead className="table-header">
                          <tr>
                            <th className="table-header-cell">Investor ID</th>
                            <th className="table-header-cell">Investor Name</th>
                            <th className="table-header-cell">Payment Amount</th>
                            <th className="table-header-cell">LEI</th>
                            <th className="table-header-cell">Percentage</th>
                            <th className="table-header-cell">Payment Type</th>
                            <th className="table-header-cell">Payment Reference</th>
                            <th className="table-header-cell">Payment Date</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {allocations.map((row, index) => (
                            <tr key={`${row['Investor ID'] || row.investorId || index}-${index}`}>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {row['Investor ID'] || row.investorId || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {row['Investor Name'] || row.investorName || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-gray-900">
                                {formatCurrency(row['Payment Amount'] || row.paymentAmount || 0)}
                              </td>
                              <td className="px-3 py-2 font-mono text-sm text-gray-500">
                                {row['LEI'] || row.lei || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-right text-gray-900">
                                {(Number(row['Percentage'] || row.percentage || 0)).toFixed(2)}%
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {row['Payment Type'] || row.paymentType || '-'}
                              </td>
                              <td className="px-3 py-2 font-mono text-sm text-gray-500">
                                {row['Payment Reference'] || row.paymentReference || '-'}
                              </td>
                              <td className="px-3 py-2 text-sm text-gray-900">
                                {row['Payment Date'] || row.paymentDate || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between px-6 py-4 bg-white border-t border-gray-200">
                <button onClick={downloadTemplate} className="btn-secondary">
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
                <div className="flex space-x-3">
                  <button onClick={closeUploadModal} className="btn-secondary">
                    Cancel
                  </button>
                  <div className="flex flex-col">
                    <button
                      onClick={handleValidateAndUpload}
                      disabled={
                        !showPreview ||
                        allocations.length === 0 ||
                        csvErrors.length > 0 ||
                        isAmountExceeded ||
                        isUploading
                      }
                      className="inline-flex items-center btn-primary"
                    >
                      {isUploading ? (
                        <>
                          <Loader className="w-4 h-4 mr-2 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Confirm Upload
                        </>
                      )}
                    </button>
                    {isAmountExceeded && (
                      <div className="mt-1 text-xs text-red-600">
                        Amount exceeds remaining balance - adjust CSV total to continue.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Approve Modal */}
      {approvingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 space-y-4 bg-white rounded-lg">
            <h3 className="text-lg font-medium text-gray-900">Approve Distribution</h3>
            <p className="text-sm text-gray-600">
              You are about to approve the distribution for{' '}
              <span className="font-medium">{approvingPayment.bondId?.bondName}</span> scheduled on{' '}
              {formatDate(approvingPayment.paymentDate)}.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setApprovingPayment(null)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmApproveL2}
                className="btn-primary"
              >
               
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {rejectingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md p-6 space-y-4 bg-white rounded-lg">
            <h3 className="text-lg font-medium text-gray-900">Reject Distribution</h3>
            <p className="text-sm text-gray-600">
              Provide a reason for rejecting the distribution for{' '}
              <span className="font-medium">{rejectingPayment.bondId?.bondName}</span>.
            </p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Rejection reason"
            />
            <textarea
              value={rejectionComments}
              onChange={(e) => setRejectionComments(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-1 focus:ring-gray-400 focus:border-gray-400"
              placeholder="Additional comments (optional)"
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setRejectingPayment(null);
                  setRejectionReason('');
                  setRejectionComments('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectionReason.trim()}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default CustodianCouponProfitDistribution;


